import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import Link from '@mui/material/Link';
import { useAuth } from '@/context/AuthContext';
import { useColorMode } from '@/theme/ThemeProvider';
import { useI18n } from '@/i18n/I18nContext';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LanguageIcon from '@mui/icons-material/Language';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import CreditsDisplay from '@/components/CreditsDisplay';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';

const Navbar: React.FC = () => {
	const { user, signOut } = useAuth();
	const { mode, toggle } = useColorMode();
	const { t, language, setLanguage } = useI18n();
	const navigate = useNavigate();
	const location = useLocation();
	const [anchorUser, setAnchorUser] = useState<null | HTMLElement>(null);
	const [anchorLang, setAnchorLang] = useState<null | HTMLElement>(null);
	const [mobileOpen, setMobileOpen] = useState<boolean>(false);

	return (
		<AppBar position="sticky" color="transparent" elevation={0}>
			<Container maxWidth="lg">
				<Toolbar disableGutters sx={{ gap: 1 }}>
					<IconButton
						size="large"
						color="inherit"
						sx={{ display: { xs: 'inline-flex', md: 'none' } }}
						onClick={() => setMobileOpen(true)}
					>
						<MenuIcon />
					</IconButton>
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						<Link
							component={RouterLink}
							to="/"
							underline="none"
							color="inherit"
							sx={{ fontWeight: 800, letterSpacing: 0.5 }}
						>
							{t.common.appName}
						</Link>
					</Typography>
					<Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center', mr: 1 }}>
						<Button color="inherit" component={RouterLink} to="/batches">
							{t.nav.batches}
						</Button>
						<Button color="inherit" component={RouterLink} to="/recipes">
							{t.nav.recipes}
						</Button>
						<Button color="inherit" component={RouterLink} to="/inventory">
							Inventory
						</Button>
						<Button color="inherit" component={RouterLink} to="/ai-assistant">
							{t.nav.aiAssistant}
						</Button>
						<Button color="inherit" component={RouterLink} to="/pricing">
							Pricing
						</Button>
					</Box>
					<FormControl size="small" sx={{ minWidth: 80, mr: 1 }}>
						<Select
							value={language}
							onChange={(e) => setLanguage(e.target.value as 'en' | 'pt')}
							sx={{
								color: 'inherit',
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'rgba(255, 255, 255, 0.23)',
								},
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'rgba(255, 255, 255, 0.5)',
								},
								'& .MuiSvgIcon-root': {
									color: 'inherit',
								},
							}}
						>
							<MenuItem value="en">EN</MenuItem>
							<MenuItem value="pt">PT</MenuItem>
						</Select>
					</FormControl>
					<Tooltip title={mode === 'light' ? t.nav.activateDarkMode : t.nav.activateLightMode}>
						<IconButton color="inherit" onClick={toggle}>
							{mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
						</IconButton>
					</Tooltip>
					{user ? (
						<>
							<Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}>
								<CreditsDisplay variant="chip" size="small" />
							</Box>
							<IconButton color="inherit" onClick={(e) => setAnchorUser(e.currentTarget)}>
								<Avatar sx={{ width: 30, height: 30 }}>{user.name[0]?.toUpperCase()}</Avatar>
							</IconButton>
							<Menu
								anchorEl={anchorUser}
								open={Boolean(anchorUser)}
								onClose={() => setAnchorUser(null)}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							>
								<MenuItem component={RouterLink} to="/dashboard" onClick={() => setAnchorUser(null)}>
									{t.nav.dashboard}
								</MenuItem>
								{user.isAdmin && (
									<MenuItem component={RouterLink} to="/admin" onClick={() => setAnchorUser(null)}>
										{t.nav.admin}
									</MenuItem>
								)}
								<MenuItem
									onClick={() => {
										setAnchorUser(null);
										signOut();
									}}
								>
									{t.nav.logout}
								</MenuItem>
							</Menu>
						</>
					) : (
						<Button color="inherit" component={RouterLink} to="/login">
							{t.nav.login}
						</Button>
					)}
					<Drawer
						variant="temporary"
						open={mobileOpen}
						onClose={() => setMobileOpen(false)}
						ModalProps={{ keepMounted: true }}
						PaperProps={{
							sx: {
								width: 320,
								p: 1,
								borderTopRightRadius: 2,
								borderBottomRightRadius: 2,
								boxShadow: 8
							}
						}}
					>
						<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Avatar variant="rounded" sx={{ width: 28, height: 28, fontWeight: 800, bgcolor: 'primary.main' }}>B</Avatar>
									<Box>
										<Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
											{t.common.appName}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{t.nav.subtitle}
										</Typography>
									</Box>
								</Box>
								<IconButton onClick={() => setMobileOpen(false)}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Divider />
							{user && (
								<Box sx={{ px: 2, py: 1 }}>
									<CreditsDisplay variant="chip" size="small" />
								</Box>
							)}
							<List
								subheader={<ListSubheader disableSticky>{t.nav.navigation}</ListSubheader>}
								sx={{ py: 0.5 }}
							>
								<ListItemButton
									component={RouterLink}
									to="/"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname === '/'}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<HomeIcon />
									</ListItemIcon>
									<Typography>{t.nav.home}</Typography>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/batches"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname.startsWith('/batches')}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<LocalBarIcon />
									</ListItemIcon>
									<Typography>{t.nav.batches}</Typography>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/recipes"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname.startsWith('/recipes')}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<MenuBookIcon />
									</ListItemIcon>
									<Typography>{t.nav.recipes}</Typography>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/inventory"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname.startsWith('/inventory')}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<InventoryIcon />
									</ListItemIcon>
									<Typography>Inventory</Typography>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/ai-assistant"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname.startsWith('/ai-assistant')}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<PsychologyIcon />
									</ListItemIcon>
									<Typography>{t.nav.aiAssistant}</Typography>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/pricing"
									onClick={() => setMobileOpen(false)}
									selected={location.pathname === '/pricing'}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<AttachMoneyIcon />
									</ListItemIcon>
									<Typography>Pricing</Typography>
								</ListItemButton>
							</List>
							<Divider />
							<List
								subheader={<ListSubheader disableSticky>{t.nav.preferences}</ListSubheader>}
								sx={{ py: 0.5 }}
							>
								<ListItemButton onClick={() => { toggle(); }} sx={{ borderRadius: 1, mx: 1, my: 0.5 }}>
									<ListItemIcon>
										{mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
									</ListItemIcon>
									<Typography>{mode === 'light' ? t.nav.darkMode : t.nav.lightMode}</Typography>
								</ListItemButton>
								<ListItemButton 
									onClick={(e) => setAnchorLang(e.currentTarget)}
									sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
								>
									<ListItemIcon>
										<LanguageIcon />
									</ListItemIcon>
									<Typography>{t.nav.language}</Typography>
									<Box sx={{ ml: 'auto' }}>
										<Typography variant="caption" color="text.secondary">
											{language.toUpperCase()}
										</Typography>
									</Box>
								</ListItemButton>
							</List>
							<Menu
								anchorEl={anchorLang}
								open={Boolean(anchorLang)}
								onClose={() => setAnchorLang(null)}
							>
								<MenuItem onClick={() => { setLanguage('en'); setAnchorLang(null); }}>
									English
								</MenuItem>
								<MenuItem onClick={() => { setLanguage('pt'); setAnchorLang(null); }}>
									Português
								</MenuItem>
							</Menu>
							<Divider />
							<List
								subheader={<ListSubheader disableSticky>{t.nav.account}</ListSubheader>}
								sx={{ py: 0.5 }}
							>
								{user ? (
									<>
										<ListItemButton
											component={RouterLink}
											to="/dashboard"
											onClick={() => setMobileOpen(false)}
											selected={location.pathname.startsWith('/dashboard')}
											sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
										>
											<ListItemIcon>
												<DashboardIcon />
											</ListItemIcon>
											<Typography>{t.nav.dashboard}</Typography>
										</ListItemButton>
										{user.isAdmin && (
											<ListItemButton
												component={RouterLink}
												to="/admin"
												onClick={() => setMobileOpen(false)}
												selected={location.pathname.startsWith('/admin')}
												sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
											>
												<ListItemIcon>
													<AdminPanelSettingsIcon />
												</ListItemIcon>
												<Typography>{t.nav.admin}</Typography>
											</ListItemButton>
										)}
										<ListItemButton
											onClick={() => {
												setMobileOpen(false);
												signOut();
												navigate('/');
											}}
											sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
										>
											<ListItemIcon>
												<LogoutIcon />
											</ListItemIcon>
											<Typography>{t.nav.logout}</Typography>
										</ListItemButton>
									</>
								) : (
									<ListItemButton
										component={RouterLink}
										to="/login"
										onClick={() => setMobileOpen(false)}
										selected={location.pathname.startsWith('/login')}
										sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
									>
										<ListItemIcon>
											<LoginIcon />
										</ListItemIcon>
										<Typography>{t.nav.login}</Typography>
									</ListItemButton>
								)}
							</List>
							<Box sx={{ mt: 'auto', px: 2, py: 1 }}>
								<Typography variant="caption" color="text.disabled">
									{t.footer.copyright}
								</Typography>
							</Box>
						</Box>
					</Drawer>
				</Toolbar>
			</Container>
		</AppBar>
	);
};

export default Navbar;
